import { Button, DialogContentText, LinearProgress, Menu, MenuItem } from '@mui/material';
import React, { useState, useCallback } from 'react';
import { updateTestStatus, getTest } from '../../../apiClients/backendApi';
import useDialog from '../../../hooks/useDialog';
import stopPropagation from '../../../utils/stopPropagation';
import TestStatuses from '@dyl/constants/TestStatuses';
import { useAuth } from '@dyl/systems/UserManager';
import { useSnackbar } from 'notistack';
import ChangeStatusConflictModal from './ChangeStatusConflictModal';

function ChangeStatusAction({ test, levelId, ...other }) {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [ImperativeDialog, openDialog, closeDialog] = useDialog();
    const { user } = useAuth();
    let username = user?.attributes?.email;
    const { enqueueSnackbar } = useSnackbar();
    const [isConflictModalOpen, setConflictModalOpen] = useState(false);
    const onActionClick = useCallback(event => {
        event.stopPropagation();
        event.preventDefault(); // Important to stop parent links activating
        setMenuAnchor(event.currentTarget);
    }, []);

    const onMenuClickAway = useCallback(() => setMenuAnchor(null), []);

    const onClickNewStatus = useCallback(async statusId => {
        setMenuAnchor(null);
        let didUserConfirm = true;
        const data  = await getTest(levelId, test.test_group_id);
        const { tests = [] } = data;
        const skillsWithNullQpvRef = tests.filter(item => item.qpv_reference === null)
            .map( item => item.skill_id);
        // If changing to live, warn first
        if (statusId === 'live') {
            const errorMessage = skillsWithNullQpvRef.map(skill => {
                const skillInCaps = skill.charAt(0).toUpperCase() + skill.slice(1);
                return `* ${skillInCaps} QPV. You can add this within the ${skillInCaps} component’s details.`;
            }).join('\n');
            if (skillsWithNullQpvRef.length > 0 && test.qpvSet === null) {
                enqueueSnackbar(
                    `In order to make this test live you must first have all of the SAP references added. The following are missing:\n* Test QPV set. You can add this within the test’s details.\n${errorMessage}`,
                    {
                        variant: 'error',
                        autoHideDuration: 60000,
                    }
                );
                return;
            }
            else if (skillsWithNullQpvRef.length > 0 || test.qpvSet === null) {
                if (test.qpvSet === null) {
                    enqueueSnackbar(
                        `In order to make this test live you must first have all of the SAP references added. The following are missing:\n* Test QPV set. You can add this within the test’s details.`,
                        {
                            variant: 'error',
                            autoHideDuration: 60000,
                        }
                    );
                    return;
                }
                enqueueSnackbar(
                    `In order to make this test live, you must first have all of the SAP references added. The following are missing:\n${errorMessage}`,
                    {
                        variant: 'error',
                        autoHideDuration: 60000,
                    }
                );
                return;
            }

            didUserConfirm = await new Promise(resolve => {
                openDialog(
                    'Warning! Publish to live?',
                    <>
                        <DialogContentText>
                            This will make the test immediately available for being taken by learners. Are you sure?
                        </DialogContentText>
                        <DialogContentText>
                            Please be aware that all non-live passwords linked to this test will be disabled when you make this test live.
                        </DialogContentText>
                    </>,
                    [
                        { text: 'Cancel', onClick: () => resolve(false) },
                        { text: 'Publish', onClick: () => resolve(true) },
                    ]
                );
            });
        // If changing to sample, warn first
        } else if (statusId === 'sample') {
            didUserConfirm = await new Promise(resolve => {
                openDialog(
                    'Warning! Change to sample?',
                    <>
                        <DialogContentText>
                            Are you sure you want to change the status of this test to Sample?
                        </DialogContentText>
                        <DialogContentText>
                            It will immediately appear with the other sample tests and will be available to all users.
                        </DialogContentText>
                    </>,
                    [
                        { text: 'Cancel', onClick: () => resolve(false) },
                        { text: 'Change', onClick: () => resolve(true), destructive: true },
                    ]
                );
            });
        }
        // If removing from live, warn first
        else if (test.status === 'live') {
            didUserConfirm = await new Promise(resolve => {
                openDialog(
                    'Warning! Remove from live?',
                    'This will stop the test being seen by learners. Are you sure?',
                    [
                        { text: 'Cancel', onClick: () => resolve(false) },
                        { text: 'Remove', onClick: () => resolve(true), destructive: true },
                    ]
                );
            });
        }

        // Cancel depending on user response
        if (!didUserConfirm) return;

        const statusName = TestStatuses.find(
            o => o.value === statusId
        )?.label;

        // Try, prompt, retry loop
        while (true) {
            openDialog(
                'Saving...',
                <>
                    <DialogContentText>
                        Updating status to {statusName}
                    </DialogContentText>

                    <LinearProgress />
                </>,
                []
            );
            try {
                await updateTestStatus(
                    test.test_group_id,
                    test.level_id ?? levelId,
                    statusId,
                    username
                );

                closeDialog();
                window.location.reload();
                return;
            }
            catch (error) {
                console.error(error);

                if (error?.response?.status === 409) {
                    closeDialog();
                    setConflictModalOpen(true);
                    return;
                } else {
                    const shouldRetry = await new Promise(resolve => {
                        openDialog(
                            'Error',
                            'Something went wrong. Try again?',
                            [
                                { text: 'Cancel', onClick: () => resolve(false) },
                                { text: 'Retry', onClick: () => resolve(true) },
                            ]
                        );
                    })

                    if (!shouldRetry) {
                        closeDialog();
                        return;
                    }
                }
            }
        }
    }, [test, levelId]);

    return (
        <div onClick={stopPropagation}>
            <Button color='primary' onClick={onActionClick} {...other}>
                Change Status
            </Button>

            <Menu
                open={!!menuAnchor}
                anchorEl={menuAnchor}
                onClose={onMenuClickAway}
            >
                {TestStatuses.map(({ label, value }) => (
                    <MenuItem
                        key={value}
                        disabled={test.status === value}
                        onClick={() => onClickNewStatus(value)}
                    >
                        {label}
                    </MenuItem>
                ))}
            </Menu>

            <ImperativeDialog />
            <ChangeStatusConflictModal
                open={isConflictModalOpen}
                onClose={() => setConflictModalOpen(false)}
            />
        </div>
    );
}

export default ChangeStatusAction;